import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { TooltipProps } from '@chakra-ui/react'
import { Tooltip } from '@chakra-ui/react'

type CopyTooltipProps = {
  /** Default hover label */
  label: string
  /** Label shown for `duration` ms after success copy */
  successLabel: string
  /** Label shown for `duration` ms after a failure */
  errorLabel?: string
  /** Static value to copy (use this OR getCopyValue) */
  copyValue?: string
  /** How long to show success/error labels (ms). Default: 500 */
  duration?: number
  /** Child must be a single interactive element */
  children: React.ReactElement
  /** Async/sync getter for value to copy (use this OR copyValue) */
  getCopyValue?(): Promise<string> | string
} & Omit<TooltipProps, 'label' | 'children' | 'isOpen'>

const CopyTooltip: React.FC<CopyTooltipProps> = ({
  label,
  successLabel,
  errorLabel = 'Failed to copy',
  copyValue,
  getCopyValue,
  duration = 500,
  children,
  ...tooltipProps
}) => {
  const [forcedOpen, setForcedOpen] = useState(false) // programmatic open
  const [hoverOpen, setHoverOpen] = useState(false) // hover-driven open
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const timeoutRef = useRef<number | null>(null)

  useEffect(
    () => () => timeoutRef.current && window.clearTimeout(timeoutRef.current),
    [],
  )

  const doCopy = async () => {
    try {
      const value = getCopyValue ? await getCopyValue() : copyValue ?? ''
      await navigator.clipboard.writeText(value)

      // while showing the success message, take full control
      setStatus('success')
      setForcedOpen(true)
      setHoverOpen(false) // suppress hover during forced state
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(() => {
        setForcedOpen(false) // close after duration
        setStatus('idle')
      }, duration)
    } catch {
      setStatus('error')
      setForcedOpen(true)
      setHoverOpen(false)
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(() => {
        setForcedOpen(false)
        setStatus('idle')
      }, duration)
    }
  }

  const enhancedChild = useMemo(() => {
    const child = React.Children.only(children) as React.ReactElement<any>
    const prevOnClick = child.props.onClick

    const handleClick = async (e: React.MouseEvent) => {
      prevOnClick?.(e)
      await doCopy()
    }

    const handleKeyDown = async (e: React.KeyboardEvent) => {
      if (child.props.onKeyDown) child.props.onKeyDown(e)
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        await doCopy()
      }
    }

    const extraInteractiveProps =
      child.type === 'button' || child.props.role === 'button'
        ? {}
        : { role: 'button', tabIndex: 0, onKeyDown: handleKeyDown }

    return React.cloneElement(child, {
      ...extraInteractiveProps,
      onClick: handleClick,
    })
  }, [children])

  const tooltipLabel =
    status === 'success'
      ? successLabel
      : status === 'error'
      ? errorLabel
      : label

  // Stay fully controlled: open if forced, otherwise only if hover AND idle
  const isOpen = forcedOpen || (status === 'idle' && hoverOpen)

  return (
    <Tooltip
      isOpen={isOpen}
      onOpen={() => void setHoverOpen(true)}
      onClose={() => void setHoverOpen(false)}
      label={tooltipLabel}
      closeOnClick={false}
      {...tooltipProps}
    >
      {enhancedChild}
    </Tooltip>
  )
}

export default CopyTooltip
